﻿import React, { useCallback, useMemo, useState } from "react";
import {
    contractsIcon,
    dashboardMenuIcon,
    dotLegalExpandedNavIcon,
    frameworkIcon,
    internalAssessments,
    legalEntitiesIcon,
    policiesIcon,
    processingActivitiesIcon,
    riskAssessmentIcon,
    systemsMenuIcon,
    taskManagementIcon,
    processesIcon,
} from "../../common/icons";
import { useNavMenuStyles } from "./NavMenu.styles";
import PrettyScrollbar from "../../common/PrettyScrollbar";
import NavigationLink from "../navigationLink/NavigationLink";
import { useTranslation } from "../../localization/useTranslation";
import { useUrlProvider } from "../../useUrlProvider";
import DotLegalLogo from "./dotLegalLogo.svg?react";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import { Box } from "@mui/material";
import NotificationDotBadge from "@common/components/notificationDotBadge/NotificationDotBadge";
import { ToDto, useHistoryWithReferer, useStateLocalStorage } from "@dotlegal/dotlegal-ui-components";
import { useFeatureFlags, usePlanContext } from "../../auth/planProvider/PlanProvider";
import { SubMenuItem } from "../navigationLink/NavigationLink.types";
import { usePermissionsProvider } from "../../auth/usePermissionsProvider";
import UpgradeCard from "./upgradeCard/UpgradeCard.tsx";
import { useOverdueTasksContext } from "../../common/providers/overdueTasksProvider/OverdueTasksProvider.tsx";
import { useRiskAreasContext } from "../../common/providers/riskAreasProvider/RiskAreasProvider.tsx";
import { FeatureFlag } from "../../auth/planProvider/PlanProvider.types.ts";
import { readRememberedRiskAreaTab } from "../../risk/riskArea/RiskArea.utils.ts";
import { readRememberedDataProcessingTab } from "../../dataProcessing/DataProcessing.utils.ts";
import { NavigationMenuItem } from "../../navigationMenuSettings/NavigationMenuSettings.types.ts";
import { isNavigationMenuItemHidden } from "../../navigationMenuSettings/navigationMenuItems.ts";

interface NavigationLinkOptions {
    menuItem: NavigationMenuItem;
    text: string;
    icon: JSX.Element;
    // Only needed when there are no sub items to link to, or as the destination when all of them are hidden.
    link?: ToDto | string;
    extraPaths: Array<string>;
    htmlId: string;
    tooltip?: string;
    subMenuItems?: Array<SubMenuItem>;
}

export interface INavMenuProps {
    isExpanded: boolean;
    onLinkClick: () => void;
    hoveringOnDrawer?: boolean;
    isExpandedBigScreen?: boolean;
    isExpandedSmallScreen?: boolean;
    trialRibbonShown?: boolean;
}

function NavMenu(props: INavMenuProps) {
    const styles = useNavMenuStyles(props);
    const urlProvider = useUrlProvider();
    const { translateString } = useTranslation();
    const userContext = useUserContext();
    const permissionsProvider = usePermissionsProvider();
    const { vendorManagementFeatures, addonsFeatures, platformFeatures, contractManagementFeatures } = usePlanContext();
    const { hasEnterpriseRiskManagementAccess } = usePermissionsProvider();
    const historyWithReferer = useHistoryWithReferer();
    const [isAccountMenuExpanded, setIsAccountMenuExpanded] = useStateLocalStorage("account-menu-expanded", true);
    const [subMenuExpanded, setSubMenuExpanded] = useState<string | false>(false);
    const { hasOverdueTasks } = useOverdueTasksContext();
    const { riskAreas } = useRiskAreasContext();
    const featureFlags = useFeatureFlags();

    const GetTaskManagementIcon = useCallback(
        (icon: JSX.Element) => <NotificationDotBadge show={hasOverdueTasks}>{icon}</NotificationDotBadge>,
        [hasOverdueTasks]
    );

    // Hidden items are filtered here rather than in each item's condition, so a new menu item is covered by the
    // customer's menu settings just by naming its NavigationMenuItem.
    const navigationLink = useCallback(
        (options: NavigationLinkOptions) => {
            const isHidden = (menuItem: NavigationMenuItem) => isNavigationMenuItemHidden(userContext.hiddenNavigationMenuItems, menuItem);
            const visibleSubMenuItems = options.subMenuItems?.filter((subMenuItem) => !isHidden(subMenuItem.menuItem)) ?? [];
            const link = visibleSubMenuItems[0]?.link ?? options.link;

            if (isHidden(options.menuItem) || link === undefined) {
                return null;
            }

            return (
                <NavigationLink
                    text={options.text}
                    icon={options.icon}
                    isExpanded={props.isExpanded}
                    linkTo={link}
                    onClick={props.onLinkClick}
                    tooltip={options.tooltip}
                    subMenuItems={visibleSubMenuItems.length > 0 ? visibleSubMenuItems : undefined}
                    extraPaths={options.extraPaths}
                    htmlId={options.htmlId}
                    subMenuExpanded={subMenuExpanded}
                    onSubMenuExpanded={setSubMenuExpanded}
                    isExpandedSmallScreen={props.isExpandedSmallScreen}
                />
            );
        },
        [props.isExpanded, props.isExpandedSmallScreen, props.onLinkClick, subMenuExpanded, userContext.hiddenNavigationMenuItems]
    );

    const regularNavLinks = useMemo(() => {
        function buildRiskSubMenuItems(): Array<SubMenuItem> {
            if (riskAreas.length === 0) return [];

            const items: Array<SubMenuItem> = [];

            if (userContext.permissions.riskPermissions.read && userContext.showLegacyRiskAssessments) {
                items.push({
                    name: translateString("riskAssessments"),
                    link: historyWithReferer.addSearchIfReferer(urlProvider.getRiskAssessmentsUrl()),
                    extraPaths: ["privacy/risk-assessments"],
                    htmlId: "risk-assessments-overview",
                    menuItem: NavigationMenuItem.Risk,
                });
            }

            items.push({
                name: translateString("riskManagementReport"),
                link: historyWithReferer.addSearchIfReferer(urlProvider.getRiskManagementReportUrl()),
                extraPaths: ["privacy/risk-management-report"],
                htmlId: "risk-management-report",
                menuItem: NavigationMenuItem.Risk,
            });

            riskAreas.forEach((riskArea) => {
                items.push({
                    name: riskArea.name,
                    link: urlProvider.getRiskAreaUrl(riskArea.id, readRememberedRiskAreaTab(riskArea.id)),
                    extraPaths: [`privacy/risk/${riskArea.id}`],
                    htmlId: `risk-area-${riskArea.id}`,
                    menuItem: NavigationMenuItem.Risk,
                });
            });

            return items;
        }

        let legalEntitiesSubMenuItems: Array<SubMenuItem> = [];
        if (userContext.permissions.companyPermissions.read) {
            legalEntitiesSubMenuItems.push({
                name: translateString("external"),
                link: historyWithReferer.addSearchIfReferer(urlProvider.getExternalLegalEntities()),
                extraPaths: ["privacy/external-legal-entities"],
                tooltip: translateString("menuItemExternalLegalEntityDescription"),
                htmlId: "external-legal-entities",
                menuItem: NavigationMenuItem.ExternalLegalEntities,
            });
        }

        if (userContext.permissions.canManageGroupEntities) {
            legalEntitiesSubMenuItems.push({
                name: translateString("groupCompanies"),
                link: historyWithReferer.addSearchIfReferer(urlProvider.getGroupEntitiesUrl()),
                extraPaths: ["privacy/group-entities"],
                tooltip: translateString("menuItemGroupEntityDescription"),
                htmlId: "group-entities",
                menuItem: NavigationMenuItem.GroupEntities,
            });
        }

        let assessmentSubMenuItems: Array<SubMenuItem> = [];
        if (userContext.permissions.canManageAuditGroups && vendorManagementFeatures.hasVendorManagement) {
            assessmentSubMenuItems.push({
                name: translateString("externalAudits"),
                link: historyWithReferer.addSearchIfReferer(urlProvider.getAuditGroupsUrl()),
                extraPaths: ["privacy/external-assessments", "privacy/audit-group"],
                tooltip: translateString("menuItemExternalAuditsDescription"),
                htmlId: "audit-groups",
                menuItem: NavigationMenuItem.ExternalAudits,
            });
        }

        if (userContext.permissions.canManageInternalAssessment && vendorManagementFeatures.hasVendorManagement) {
            assessmentSubMenuItems.push({
                name: translateString("internalAssessments"),
                link: historyWithReferer.addSearchIfReferer(urlProvider.getInternalAssessmentUrl()),
                extraPaths: ["privacy/internal-assessments", "privacy/assessment-group"],
                tooltip: translateString("menuItemInternalAuditsDescription"),
                htmlId: "internal-assessments",
                menuItem: NavigationMenuItem.InternalAssessments,
            });
        }

        // The new processing activity page holds the record as one of its views, so it takes a single menu item
        // instead of the Processes group. Record-only users cannot open that page and keep the group with the record.
        // SIMPLIFY when FeatureFlag.NewProcessingActivity is removed - keep only the single processing activities item
        const showSingleProcessingActivitiesItem =
            featureFlags.isEnabled(FeatureFlag.NewProcessingActivity) && userContext.permissions.processingActivityPermissions.read;

        let processingSubMenuItems: Array<SubMenuItem> = [];
        if (userContext.permissions.processingActivityPermissions.read) {
            processingSubMenuItems.push({
                name: translateString("processingActivities"),
                link: historyWithReferer.addSearchIfReferer(urlProvider.getProcessingActivitiesOverviewUrl()),
                tooltip: translateString("menuItemProcessingActivitiesDescription"),
                extraPaths: ["privacy/processingactivity"],
                htmlId: "processing-activities",
                menuItem: NavigationMenuItem.ProcessingActivities,
            });
        }

        if (userContext.permissions.canAccessRecord301 || userContext.permissions.canAccessRecord302) {
            processingSubMenuItems.push({
                name: translateString("record"),
                link: historyWithReferer.addSearchIfReferer(urlProvider.getRecordUrl()),
                tooltip: translateString("menuItemRecordDescription"),
                extraPaths: ["privacy/record"],
                htmlId: "processing-activity-record",
                menuItem: NavigationMenuItem.Record,
            });
        }

        let taskManagementSubMenuItems: Array<SubMenuItem> = [];
        if (permissionsProvider.hasTaskManagementAccess()) {
            taskManagementSubMenuItems.push({
                name: translateString("tasks"),
                link: historyWithReferer.addSearchIfReferer(urlProvider.getTaskManagementUrl()),
                tooltip: translateString("menuItemTasksDescription"),
                extraPaths: ["privacy/task-management"],
                htmlId: "tasks",
                menuItem: NavigationMenuItem.Tasks,
            });
        }

        if (permissionsProvider.hasAnnualWheelActivityAccess()) {
            if (!featureFlags.isEnabled(FeatureFlag.HideAnnualWheel)) {
                taskManagementSubMenuItems.push({
                    name: translateString("annualWheel"),
                    link: historyWithReferer.addSearchIfReferer(urlProvider.getAnnualWheelUrl()),
                    tooltip: translateString("menuItemAnnualWheelDescription"),
                    extraPaths: ["privacy/annual-wheel"],
                    htmlId: "annual-wheel",
                    menuItem: NavigationMenuItem.AnnualWheel,
                });
            }

            if (featureFlags.isEnabled(FeatureFlag.Activities))
                taskManagementSubMenuItems.push({
                    name: translateString("activities"),
                    link: historyWithReferer.addSearchIfReferer(urlProvider.getActivitiesUrl()),
                    tooltip: translateString("menuItemActivitiesDescription"),
                    extraPaths: ["privacy/activities"],
                    htmlId: "activities",
                    menuItem: NavigationMenuItem.Activities,
                });
        }

        let policyAndProceduresSubmenuItems: Array<SubMenuItem> = [];
        if (userContext.permissions.policyPermissions.read) {
            policyAndProceduresSubmenuItems.push({
                name: translateString("policies"),
                link: historyWithReferer.addSearchIfReferer(urlProvider.getPoliciesUrl()),
                tooltip: translateString("menuItemPolicyDescription"),
                extraPaths: ["privacy/policies", "privacy/policy"],
                htmlId: "policies",
                menuItem: NavigationMenuItem.Policies,
            });
        }

        if (userContext.permissions.incidentLogPermissions.read) {
            policyAndProceduresSubmenuItems.push({
                name: translateString("incidentLog"),
                link: historyWithReferer.addSearchIfReferer(urlProvider.getIncidentLogUrl()),
                tooltip: translateString("incidentLogNavMenuDescription"),
                extraPaths: ["privacy/incident-log"],
                htmlId: "incident-log",
                menuItem: NavigationMenuItem.IncidentLog,
            });
        }

        let dashboardSubMenuItems: Array<SubMenuItem> = [];
        if (platformFeatures.dashboard) {
            dashboardSubMenuItems.push({
                name: translateString("overview"),
                link: historyWithReferer.addSearchIfReferer(urlProvider.getDashboardUrl()),
                tooltip: translateString("menuItemDashboardDescription"),
                htmlId: "dashboard-overview",
                menuItem: NavigationMenuItem.DashboardOverview,
            });
        }

        if (contractManagementFeatures.hasContractManagement && userContext.permissions.contractManagementPermissions?.canAccessContractsOverview) {
            dashboardSubMenuItems.push({
                name: translateString("contracts"),
                link: historyWithReferer.addSearchIfReferer(urlProvider.getContractDashboardUrl()),
                tooltip: translateString("menuItemContractDashboardDescription"),
                htmlId: "contract-dashboard",
                menuItem: NavigationMenuItem.ContractDashboard,
            });
        }

        const riskSubMenuItems = buildRiskSubMenuItems();

        return (
            <>
                {/* SIMPLIFY when FeatureFlag.ContractDashboard is removed - keep only the Dashboard group */}
                {featureFlags.isEnabled(FeatureFlag.ContractDashboard)
                    ? navigationLink({
                          menuItem: NavigationMenuItem.Dashboard,
                          text: translateString("dashboard"),
                          icon: dashboardMenuIcon,
                          extraPaths: ["privacy/dashboard"],
                          htmlId: "dashboard",
                          tooltip: translateString("menuItemDashboardDescription"),
                          subMenuItems: dashboardSubMenuItems,
                      })
                    : platformFeatures.dashboard &&
                      navigationLink({
                          menuItem: NavigationMenuItem.DashboardOverview,
                          text: translateString("dashboard"),
                          icon: dashboardMenuIcon,
                          link: historyWithReferer.addSearchIfReferer(urlProvider.getDashboardUrl()),
                          extraPaths: ["privacy/dashboard"],
                          htmlId: "dashboard",
                          tooltip: translateString("menuItemDashboardDescription"),
                      })}

                {userContext.permissions.frameworkPermissions.read &&
                    navigationLink({
                        menuItem: NavigationMenuItem.Frameworks,
                        text: translateString("frameworks"),
                        icon: frameworkIcon,
                        link: historyWithReferer.addSearchIfReferer(urlProvider.getFrameworksUrl()),
                        extraPaths: [],
                        htmlId: "frameworks",
                        tooltip: translateString("frameworkTooltip"),
                    })}

                {showSingleProcessingActivitiesItem
                    ? navigationLink({
                          menuItem: NavigationMenuItem.ProcessingActivities,
                          text: translateString("processingActivities"),
                          icon: processingActivitiesIcon,
                          link: historyWithReferer.addSearchIfReferer(urlProvider.getDataProcessingUrl(readRememberedDataProcessingTab())),
                          extraPaths: ["privacy/processingactivity"],
                          htmlId: "processing-activities",
                          tooltip: translateString("menuItemProcessingActivitiesDescription"),
                      })
                    : navigationLink({
                          menuItem: NavigationMenuItem.Processes,
                          text: translateString("processes"),
                          icon: processingActivitiesIcon,
                          extraPaths: ["privacy/processingactivity", "privacy/record"],
                          htmlId: "processes",
                          tooltip: translateString("menuItemProcessesDescription"),
                          subMenuItems: processingSubMenuItems,
                      })}

                {featureFlags.isEnabled(FeatureFlag.ProcessTemplates) &&
                    userContext.permissions.processPermissions.read &&
                    navigationLink({
                        menuItem: NavigationMenuItem.ProcessTemplates,
                        text: translateString("processes"),
                        icon: processesIcon,
                        link: historyWithReferer.addSearchIfReferer(urlProvider.getProcessesUrl()),
                        extraPaths: [],
                        htmlId: "processes",
                        tooltip: translateString("menuItemProcessesDescription"),
                    })}

                {userContext.permissions.systemPermissions.read &&
                    navigationLink({
                        menuItem: NavigationMenuItem.Systems,
                        text: translateString("systems"),
                        icon: systemsMenuIcon,
                        link: historyWithReferer.addSearchIfReferer(urlProvider.getSystemsUrl()),
                        extraPaths: ["privacy/systems", "privacy/system"],
                        htmlId: "assets",
                        tooltip: translateString("menuItemSystemDescription"),
                    })}

                {((userContext.permissions.riskPermissions.read && userContext.showLegacyRiskAssessments) || hasEnterpriseRiskManagementAccess) &&
                    navigationLink({
                        menuItem: NavigationMenuItem.Risk,
                        text: translateString("risk"),
                        icon: riskAssessmentIcon,
                        // With no risk areas the submenu is empty, so this parent has to carry a destination of its
                        // own. It cannot be the legacy overview once the customer has hidden it.
                        link: historyWithReferer.addSearchIfReferer(
                            userContext.showLegacyRiskAssessments ? urlProvider.getRiskAssessmentsUrl() : urlProvider.getRiskManagementReportUrl()
                        ),
                        extraPaths: ["privacy/risk-assessments", "privacy-riskassessments/template", "privacy/risk"],
                        htmlId: "risk-assessments",
                        tooltip: translateString("menuItemRiskWithISMSDescription"),
                        subMenuItems: riskSubMenuItems,
                    })}

                {contractManagementFeatures.hasContractManagement &&
                    userContext.permissions.contractManagementPermissions?.canAccessContractsOverview &&
                    navigationLink({
                        menuItem: NavigationMenuItem.Contracts,
                        text: translateString("contracts"),
                        icon: contractsIcon,
                        link: historyWithReferer.addSearchIfReferer(urlProvider.getContractsUrl()),
                        extraPaths: ["privacy/contracts"],
                        htmlId: "contracts",
                        tooltip: translateString("contractMenuTooltip"),
                    })}

                {navigationLink({
                    menuItem: NavigationMenuItem.TaskManagement,
                    text: translateString("taskManagement"),
                    icon: GetTaskManagementIcon(taskManagementIcon),
                    extraPaths: ["privacy/annual-wheel", "privacy/task-management", "privacy/activities"],
                    htmlId: "task-management",
                    tooltip: translateString("menuItemTaskManagementDescription"),
                    subMenuItems: taskManagementSubMenuItems,
                })}

                {navigationLink({
                    menuItem: NavigationMenuItem.Documentation,
                    text: translateString("documentation"),
                    icon: policiesIcon,
                    extraPaths: ["privacy/policies", "privacy/policy", "privacy/incident-log"],
                    htmlId: "incident-log",
                    tooltip: translateString("menuItemPolicyAndProceduresDescription"),
                    subMenuItems: policyAndProceduresSubmenuItems,
                })}

                {navigationLink({
                    menuItem: NavigationMenuItem.LegalEntities,
                    text: translateString("legalEntities"),
                    icon: legalEntitiesIcon,
                    extraPaths: ["privacy/legal-entities", "privacy/group-entities", "privacy/external-legal-entities"],
                    htmlId: "legal-entities",
                    tooltip: translateString("menuItemLegalEntityDescription"),
                    subMenuItems: legalEntitiesSubMenuItems,
                })}

                {navigationLink({
                    menuItem: NavigationMenuItem.AuditsAndAssessments,
                    text: translateString("auditsAndAssessments"),
                    icon: internalAssessments,
                    extraPaths: ["privacy/internal-assessments", "privacy/audit-groups", "privacy/audit-group", "privacy/assessment-group"],
                    htmlId: "audits-and-assessments",
                    tooltip: translateString("menuItemAuditsDescription"),
                    subMenuItems: assessmentSubMenuItems,
                })}
            </>
        );
    }, [
        userContext.permissions.companyPermissions.read,
        userContext.permissions.canManageGroupEntities,
        userContext.permissions.canManageAuditGroups,
        userContext.permissions.canManageInternalAssessment,
        userContext.permissions.processingActivityPermissions.read,
        userContext.permissions.canAccessRecord301,
        userContext.permissions.canAccessRecord302,
        userContext.permissions.frameworkPermissions.read,
        userContext.permissions.policyPermissions.read,
        userContext.permissions.incidentLogPermissions.read,
        userContext.permissions.processPermissions.read,
        userContext.permissions.systemPermissions.read,
        userContext.permissions.riskPermissions.read,
        riskAreas,
        userContext.permissions.contractManagementPermissions?.canAccessContractsOverview,
        vendorManagementFeatures.hasVendorManagement,
        permissionsProvider,
        platformFeatures.dashboard,
        navigationLink,
        translateString,
        historyWithReferer,
        urlProvider,
        featureFlags,
        contractManagementFeatures,
        GetTaskManagementIcon,
        hasEnterpriseRiskManagementAccess,
        userContext.showLegacyRiskAssessments,
    ]);

    return (
        <Box sx={styles.innerContainer}>
            <PrettyScrollbar>
                <Box sx={styles.list}>
                    <Box>
                        <Box id={"regular-nav-links"} component={"ul"} sx={styles.navList}>
                            {regularNavLinks}
                        </Box>
                        {(props.isExpandedBigScreen || props.isExpandedSmallScreen) && <UpgradeCard />}
                    </Box>
                    <Box sx={styles.logo}>
                        <a href="https://dotlegal.dk">{props.isExpanded ? dotLegalExpandedNavIcon : <DotLegalLogo />}</a>
                    </Box>
                </Box>
            </PrettyScrollbar>
        </Box>
    );
}

export default NavMenu;
